import styled, { keyframes } from 'styled-components';
import { Box, theme } from '@frameio/vapor';

type props = {
  state: 'BLANK' | 'REPEAT';
};

const PROGRESS_HEIGHT = 2;
const BAR_TOPOFFSET = 30;
const BAR_WIDTH = 140;
const DURATION_IN_SECONDS = 3;

const progressBarRGBA = (a = 1) => {
  return `rgba(94, 90, 205, ${a})`;
};

/**
 * Isolated component animates a line for connections or authorization
 *
 * The animation uses two distinct gradients which are moved across, in sequence.
 * A filter blur is added to evoke a subtle glow.
 */
export const ConnectProgressBar = ({ state }: props) => {
  return (
    <BarContainer $paddingTop={BAR_TOPOFFSET}>
      <Bar />
      {state === 'REPEAT' && (
        <>
          <ProgressBlurOverlay $side="left" data-chromatic="ignore" />
          <Progress $side="left" data-chromatic="ignore" />
          <ProgressBlurOverlay $side="right" data-chromatic="ignore" />
          <Progress $side="right" data-chromatic="ignore" />
        </>
      )}
    </BarContainer>
  );
};

const BarContainer = styled(Box)<{ $paddingTop: number }>`
  display: flex;
  height: 100%;
  justify-items: center;
  padding-top: ${(p) => p.$paddingTop}px;
  position: absolute;
  width: auto;
`;

const animateAcrossLeft = keyframes`
 0% { 
  opacity: 0;
  background-position: -25px 0%; 
}
3% {
  opacity: 1;
}
45% {
  opacity: 1;
}
50% { 
  opacity: 0;
  background-position: 45px 0%;
}
51% {
  opacity: 0;
  background-position: -30px 0%; 
  
}
`;

const animateAcrossRight = keyframes`
0% {
  opacity: 0;
}
56% { 
  opacity: 0.2;
  background-position: 54px 0%; 
}
58% {
  opacity: 1;
}
90% {
  opacity: 1;
}
 100% { 
  opacity: 0;
  background-position: 127px 0%; 
  }
`;

const Bar = styled.div`
  background: ${theme.colors.border.secondary};
  height: ${PROGRESS_HEIGHT}px;
  left: 0px;
  position: absolute;
  transform: translate(-50%, 0);
  width: ${BAR_WIDTH}px;
`;

const Progress = styled(Bar)<{ $side: 'left' | 'right' }>`
  animation: ${(p) =>
    p.$side === 'left' ? animateAcrossLeft : animateAcrossRight};

  animation-duration: ${DURATION_IN_SECONDS}s;
  animation-iteration-count: infinite;
  animation-timing-function: ${theme.transitions.eases.easeInOut};
  background: linear-gradient(
    90deg,
    ${progressBarRGBA(0)} 0px,
    ${progressBarRGBA(0.7)} 20px,
    ${progressBarRGBA(1)} 25px,
    ${progressBarRGBA(0)} 27px
  );

  background-position: 0% 0%;
  background-repeat: no-repeat;
  opacity: 0;
`;

const ProgressBlurOverlay = styled(Progress)`
  filter: blur(2px);
`;
